/**
 * Who is signed in, and what they may do.
 *
 * `can` is the same function the server's guards call, imported from
 * `@dp/shared` — so a control the browser hides is a control the server would
 * refuse, and neither has its own opinion. The browser's copy decides what to
 * *draw*; the server's decides what to *allow*, on every request.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { allows, type Area, type AreaDescription, type Capability, type Grants, type Preset } from "@dp/shared";
import { api } from "./api.ts";

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  grants: Grants;
}

interface AuthState {
  user: CurrentUser | null;
  areas: AreaDescription[];
  presets: Preset[];
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Whether this person may do this. Same answer the server gives. */
  can: (area: Area, capability?: Capability) => boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

interface MeResponse {
  user: CurrentUser | null;
  areas: AreaDescription[];
  presets: Preset[];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [areas, setAreas] = useState<AreaDescription[]>([]);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      // /api/iam/me needs a session; /api/auth/me answers for anyone. Ask the
      // cheap one first so a signed-out visitor gets the login screen rather
      // than a 401 in the console.
      const session = await api.get<{ user: CurrentUser | null }>("/api/auth/me");
      if (!session.user) {
        setUser(null);
        return;
      }
      const me = await api.get<MeResponse>("/api/iam/me");
      setUser(me.user);
      setAreas(me.areas);
      setPresets(me.presets);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    void load().finally(() => setLoading(false));
  }, [load]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      await api.post<{ user: CurrentUser }>("/api/auth/login", { email, password });
      await load();
    },
    [load],
  );

  const signOut = useCallback(async () => {
    await api.post("/api/auth/logout");
    setUser(null);
  }, []);

  const can = useCallback(
    (area: Area, capability: Capability = "view") => allows(user, area, capability),
    [user],
  );

  const value = useMemo(
    () => ({ user, areas, presets, loading, signIn, signOut, can, refresh: load }),
    [user, areas, presets, loading, signIn, signOut, can, load],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth used outside AuthProvider");
  return context;
}

/** A short description of what someone is, for a header or a table cell. */
export function describeAccess(user: { isAdmin: boolean; grants: Grants } | null): string {
  if (!user) return "";
  if (user.isAdmin) return "Administrator";

  const can = (area: Area, capability: Capability) => allows(user, area, capability);
  if (can("configuration", "edit") && can("products", "edit")) return "Configuration engineer";
  if (can("applications", "edit")) return "Underwriter";
  if (can("accounts", "edit")) return "Account manager";
  if (can("applications", "view") || can("products", "view")) return "Viewer";
  return "No access yet";
}
